import React from "react";

import { MoreIcon, Typography } from "@packages/ds";
import { FormattedMessage } from "react-intl";

import * as Dropdown from "ds/Dropdown";
import * as Table from "ds/Table";
import Tag from "ds/Tag";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { sortEnvironmentTypes } from "Libs/utils";
import { sortedUserAccessAndInvitationsByProjectIdSelector } from "Reducers/userAccess/userAccess.selectors";

import * as S from "./AccessTableNoOrg.styles";

type AccessTableSortKeys = {
  email: string;
  name: string;
};

type Props = {
  currentLoggedInMemberId: string;
  onSelectUserToRemove: (user: { id: string; name: string }) => void;
  projectOwnerId: string;
};

export const AccessTableNoOrg = (props: Props) => {
  const [scrollPosition, onScroll] = Table.useScrollPosition();
  const [sort, setSort] = Table.useSort<AccessTableSortKeys>({
    key: "email",
    direction: "asc"
  });
  const sortedAccesses = useSelectorWithUrlParams(
    sortedUserAccessAndInvitationsByProjectIdSelector,
    sort
  );

  return (
    <Table.TableFlex>
      <Table.Thead hasBorderBottom={scrollPosition > 0}>
        <Table.Th
          width="30%"
          onSortChanged={() => setSort("name")}
          isSortActive={sort.key === "name"}
          sortDirection={sort.direction}
        >
          <FormattedMessage id="user_name" />
        </Table.Th>
        <Table.Th
          width="20%"
          onSortChanged={() => setSort("email")}
          isSortActive={sort.key === "email"}
          sortDirection={sort.direction}
        >
          <FormattedMessage id="email" />
        </Table.Th>
      </Table.Thead>
      <Table.Tbody onScroll={onScroll}>
        {sortedAccesses.map(access => {
          const isInvitation = access.name === "";
          const kind = isInvitation ? "invitations" : "users";
          const isAdmin = access.permissions.includes("admin");

          return (
            <S.Row $isInvitation={isInvitation} key={access.id}>
              <Table.Td width="30%">
                {isInvitation ? (
                  <Tag variant="slate">
                    <FormattedMessage id="invitation.pending" />
                  </Tag>
                ) : (
                  <>
                    <Typography.BodyText size="small" weight="semiBold">
                      {access.name}
                    </Typography.BodyText>
                    {access.id === props.projectOwnerId && (
                      <>
                        <S.TextSeparation />
                        <Tag variant="slate">
                          <FormattedMessage id="organization.member.owner" />
                        </Tag>
                      </>
                    )}
                  </>
                )}
              </Table.Td>

              <Table.Td width="35%">
                <Typography.BodyText size="small">
                  {access.email}
                </Typography.BodyText>
              </Table.Td>
              <S.AccessCell width="35%">
                {isAdmin ? (
                  <S.Column>
                    <Typography.BodyText size="small">
                      <FormattedMessage id="invitation.table.admin" />
                    </Typography.BodyText>
                    <S.GraniteText size="extraSmall" weight="semiBold">
                      <FormattedMessage id="invitation.table.project" />
                    </S.GraniteText>
                  </S.Column>
                ) : (
                  access.permissions
                    .filter(permission => permission.includes(":"))
                    .sort(sortEnvironmentTypes)
                    .map(permission => {
                      const [environmentType, accessLevel] =
                        permission.split(":");
                      return (
                        <S.Column key={environmentType}>
                          <Typography.BodyText size="small">
                            <FormattedMessage
                              id={`access.options.${accessLevel}`}
                            />
                          </Typography.BodyText>
                          <S.GraniteText size="extraSmall" weight="semiBold">
                            <FormattedMessage
                              id={`access.options.${environmentType}`}
                            />
                          </S.GraniteText>
                        </S.Column>
                      );
                    })
                )}
              </S.AccessCell>
              <Table.Td width="32px">
                <Dropdown.Root id="access_table">
                  <Dropdown.Trigger aria-label="access-table-more-trigger">
                    <MoreIcon aria-label="access-table-more-trigger-icon" />
                  </Dropdown.Trigger>
                  <Dropdown.Portal>
                    <Dropdown.Content align="end">
                      <Dropdown.LinkItem to={`${kind}/${access.id}`}>
                        <FormattedMessage id="project.access.view" />
                      </Dropdown.LinkItem>
                      {access.id === props.currentLoggedInMemberId && (
                        <Dropdown.Item
                          disabled={access.id === props.projectOwnerId}
                          onSelect={() => {
                            props.onSelectUserToRemove({
                              id: access.id,
                              name: access.name
                            });
                          }}
                        >
                          <FormattedMessage id="project.access.leave" />
                        </Dropdown.Item>
                      )}
                    </Dropdown.Content>
                  </Dropdown.Portal>
                </Dropdown.Root>
              </Table.Td>
            </S.Row>
          );
        })}
      </Table.Tbody>
    </Table.TableFlex>
  );
};
